import { Mutator } from 'final-form';
import React, { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router';

import { FieldGroup } from '@xsolla/ui-core';
import { Trash } from '@xsolla/ui-icons';

import { getAgeRestrictions, updateBatchAgeRestrictions } from '../../../api';
import { AgeRestrictions } from '../../../api/configurations/legalSettings/types';
import { Error } from '../../../api/setup/getApiError';
import { useNotifications } from '../../../hooks/useNotifications';
import { usePaths } from '../../../routes/routesHelper';
import { VerticalHorizontalCenterBlock } from '../../../styled';
import { availableCountries, linkToUnderline } from '../../../utils';
import {
  FieldBlock,
  FieldInputNumber,
  FormFooter,
  HeaderTitle,
  MultiSelect,
  Preloader,
  ToDashboardError
} from '../../ui';
import { StyledButtonSquare } from './style';
import { FormValues, ValidationErrors } from './types';
import { AgeRestrictionsValidator } from './validator';

type Props = RouteComponentProps & {
  projectId: string;
};

const AgeRestrictionsForm = ({ projectId, history }: Props): JSX.Element => {
  const { navigationPath, userSupplementaryInfoPath } = usePaths();
  const intl = useIntl();
  const [ageRestrictions, setAgeRestrictions] = useState<AgeRestrictions | null>(null);
  const [fetching, setFetching] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorData, setErrorData] = useState<Error | null>(null);
  const { showErrorNotify, showSuccessNotify } = useNotifications();

  useEffect(() => {
    getAgeRestrictions(projectId)
      .then((data) => {
        setAgeRestrictions(data);
        setFetching(false);
      })
      .catch((error) => setErrorData(error));
  }, []);

  const globalRestriction = useMemo(() => {
    if (!ageRestrictions) return null;

    const global = ageRestrictions.find((el) => el.country_code === 'XX');

    return global?.age || null;
  }, [ageRestrictions]);

  const selectedCountries = useMemo(() => {
    if (!ageRestrictions) return [];

    return ageRestrictions
      .filter((elem) => elem.country_code !== 'XX')
      .reduce((previousValue, currentValue) => {
        return [...previousValue, currentValue.country_code];
      }, []);
  }, [ageRestrictions]);

  const restrictions = useMemo(() => {
    if (!ageRestrictions) return null;

    return ageRestrictions
      .filter((elem) => elem.country_code !== 'XX')
      .reduce((previousValue, currentValue) => {
        return { ...previousValue, [currentValue.country_code]: Number(currentValue.age) };
      }, {});
  }, [ageRestrictions]);

  const countiesList = useMemo(
    () =>
      availableCountries
        .map((country) => ({
          children: intl.formatMessage({ id: `form.select.country-name.${country}` }),
          'data-test-id': country,
          value: country
        }))
        .sort((a, b) => a.children.localeCompare(b.children)),
    []
  );

  const backToNavigation = () => history.push({ pathname: navigationPath(projectId) });

  const onSubmit = async (values: FormValues) => {
    try {
      setSubmitting(true);

      const {
        global,
        selectedCountries: selectedCountriesValue,
        restrictions: restrictionsValue
      } = values;

      const prepareData: AgeRestrictions = Object.entries(restrictionsValue || [])
        .filter(([key]) => selectedCountriesValue.includes(key))
        .map(([key, value]) => {
          return {
            age: Number(value),
            country_code: key
          };
        });

      if (global) {
        prepareData.push({ age: Number(global), country_code: 'XX' });
      }

      await updateBatchAgeRestrictions(projectId, prepareData);

      showSuccessNotify();
    } catch (error) {
      showErrorNotify(error);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteCountry: Mutator<FormValues> = (args, state, utils) => {
    const [countryToDelete] = args;
    utils.changeValue(state, 'selectedCountries', (prev) => {
      return prev.filter((country: string) => country !== countryToDelete);
    });
  };

  const getErrorMessage = (fieldName: string, errors?: unknown) => {
    if (!errors) return '';

    const valErrors = errors as ValidationErrors;
    const getError =
      fieldName === 'global' ? valErrors.global : valErrors.restrictions?.[fieldName];

    if (!getError) return '';

    return intl.formatMessage(
      { id: getError },
      { number: getError === 'app.validation.value-more' ? 100 : 0 }
    );
  };

  if (fetching) {
    return (
      <VerticalHorizontalCenterBlock>
        <Preloader />
      </VerticalHorizontalCenterBlock>
    );
  }

  if (errorData) {
    return <ToDashboardError pageName="terms-and-policies_open" error={errorData} />;
  }

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={{ global: globalRestriction, selectedCountries, restrictions }}
      validate={AgeRestrictionsValidator}
      mutators={{ deleteCountry }}>
      {({ handleSubmit, values, form, errors }) => {
        return (
          <>
            <HeaderTitle
              title={intl.formatMessage({ id: 'age-restrictions.settings.title' })}
              subtitle={intl.formatMessage({ id: 'age-restrictions.settings.subtitle' })}
              notification={intl.formatMessage(
                { id: 'age-restrictions.settings.notification' },
                linkToUnderline({ href: userSupplementaryInfoPath(projectId) })
              )}
            />

            <FieldBlock>
              <FieldGroup
                size="sm"
                label={intl.formatMessage({
                  id: 'age-restrictions.settings.all-countries.label'
                })}>
                <FieldInputNumber
                  name="global"
                  hideArrows
                  errorMessage={getErrorMessage('global', errors)}
                />
              </FieldGroup>
              <FieldGroup
                size="sm"
                label={intl.formatMessage({
                  id: 'age-restrictions.settings.specific-countries.label'
                })}>
                <MultiSelect
                  dataTestId="age-restrictions-select-countries"
                  name="selectedCountries"
                  items={countiesList}
                  placeholder={intl.formatMessage({
                    id: 'age-restrictions.settings.specific-countries.placeholder'
                  })}
                />
              </FieldGroup>

              {values.selectedCountries.map((country: string) => {
                return (
                  <FieldGroup
                    key={country}
                    size="sm"
                    label={intl.formatMessage({ id: `form.select.country-name.${country}` })}>
                    <FieldInputNumber
                      hideArrows
                      name={`restrictions.${country}`}
                      extraRight={
                        // eslint-disable-next-line react/jsx-wrap-multilines
                        <StyledButtonSquare
                          data-test-id={`age-restrictions-country-delete-button-${country}`}
                          palette="muted"
                          onClick={() => form.mutators.deleteCountry(country)}>
                          <Trash />
                        </StyledButtonSquare>
                      }
                      placeholder={intl.formatMessage({
                        id: 'age-restrictions.settings.country.placeholder'
                      })}
                      errorMessage={getErrorMessage(country, errors)}
                    />
                  </FieldGroup>
                );
              })}
            </FieldBlock>

            <FormFooter onSubmit={handleSubmit} onCancel={backToNavigation} fetching={submitting} />
          </>
        );
      }}
    </Form>
  );
};

export default withRouter(AgeRestrictionsForm);
